import {
  COLOR,
  MAINTENANCE_SERVICE_ORDER_PRIORITY,
  MAINTENANCE_SERVICE_ORDER_STATUS,
} from '../../config/constants';
import { DATE_FORMAT, DATE_TIME_FORMAT } from '../../config/locale';
import LogoCiva from '../../resources/images/logo-civa.png';
import { tzNormalizeDate } from '../date';
import {
  x,
  y,
  createPdfDocument,
  addText,
  addImage,
  paragraphSize,
  titleSize,
  setFont,
  isNewPageNeeded,
  cellHeight,
  textSpacing,
  subtitleSize,
  reduceText,
} from '../pdf';

const initialY = y;
const newTitleSize = titleSize - 2;

// Configuración de diseño compacto
const ROW_HEIGHT = 14;
const ACTIVITY_COLUMN_WIDTH = 55;
const ACTIVITY_MAX_LINES = 3;
const ACTIVITY_LINE_HEIGHT = 3.5;
const RESULT_HEIGHT = 25;
const RESULT_SPACING = 2;
const ROW_SPACING = 2;
const RESULT_TOP_SPACING = 1;
const COMMENT_PADDING = 2;

let myY = initialY;
let pageWidth = 0;
let page = 1;
let serviceOrderId = 0;

const printTableHeader = (doc) => {
  doc.setFontSize(subtitleSize);
  setFont(doc, 'bold');

  addText(
    doc,
    'Actividades de Mantenimiento',
    pageWidth / 2,
    (myY += textSpacing * 2),
    pageWidth,
  );

  const headerY = myY + 1.5;

  doc.rect(x, headerY, pageWidth - 10, cellHeight);

  [22, 38, 68, 85, 144, 160, 191].forEach((position) => {
    doc.line(position, headerY, position, headerY + cellHeight);
  });

  doc.setFontSize(paragraphSize);

  myY = headerY + textSpacing;

  addText(doc, 'Fecha', 13, myY, 16, 'center');
  addText(doc, 'Tipo Act.', 30, myY, 16, 'center');
  addText(doc, 'Subcategoria', 53, myY, 28, 'center');
  addText(doc, 'ID Act.', 76, myY, 17, 'center');
  addText(doc, 'Nombre de Actividad', 115, myY, 55, 'center');
  addText(doc, 'Prioridad', 152, myY, 16, 'center');
  addText(doc, 'Reportador', 175, myY, 28, 'center');
  addText(doc, 'ID', 198, myY, 12, 'center');

  myY = headerY + cellHeight;

  setFont(doc);
};

const printDocumentHeader = (doc, data) => {
  const {
    registeredBus,
    companyResource,
    status,
    priority,
    createDate,
    deadline,
    lastUpdate,
    description,
  } = data;

  const companyBusId = registeredBus ? registeredBus.companyBusId : null;
  const resourceName = companyResource ? companyResource.name : null;

  const statusConfig = MAINTENANCE_SERVICE_ORDER_STATUS[status] || {};

  const orderPriorityConfig =
    MAINTENANCE_SERVICE_ORDER_PRIORITY[priority] || {};

  addImage(doc, LogoCiva, x, myY - 2, 35, 10);

  doc.setFontSize(newTitleSize);
  setFont(doc, 'bold');

  addText(
    doc,
    'Departamento de Mantenimiento - CIVA',
    pageWidth / 2,
    (myY += 5),
    pageWidth,
  );

  addText(
    doc,
    `Orden de Servicio: #${serviceOrderId}`,
    pageWidth / 2,
    (myY += 8),
    pageWidth,
  );

  doc.setFontSize(paragraphSize);
  setFont(doc, 'bold');

  // Bus / Unidad
  if (registeredBus) {
    addText(doc, 'Bus / Unidad:', 35, (myY += textSpacing), 30, 'right');
  }

  addText(doc, companyBusId || resourceName || '-', 45, myY, 30);

  addText(doc, 'Fecha de Creacion:', 173, myY, 35, 'right');

  setFont(doc);

  addText(
    doc,
    tzNormalizeDate({
      date: createDate,
      format: DATE_TIME_FORMAT,
    }),
    190,
    myY,
    30,
  );

  // Estado
  setFont(doc, 'bold');

  addText(doc, 'Estado:', 35, (myY += textSpacing), 30, 'right');

  doc.setTextColor(statusConfig.hexColor || COLOR.black);

  addText(doc, statusConfig.label || '-', 45, myY, 30);

  doc.setTextColor(COLOR.black);
  setFont(doc);

  // Fecha límite
  addText(doc, 'Fecha Limite:', 173, myY, 35, 'right');

  addText(
    doc,
    tzNormalizeDate({
      date: deadline,
      format: DATE_TIME_FORMAT,
    }),
    190,
    myY,
    30,
  );

  // Prioridad
  addText(doc, 'Prioridad:', 35, (myY += textSpacing), 30, 'right');

  addText(doc, orderPriorityConfig.label || '-', 45, myY, 30);

  // Número de página
  addText(doc, 'Hoja:', 173, myY, 35, 'right');

  addText(doc, page.toString(), 190, myY, 30);

  // Última actualización
  addText(
    doc,
    'Fecha Ult. Actualizacion:',
    173,
    (myY += textSpacing),
    35,
    'right',
  );

  addText(
    doc,
    tzNormalizeDate({
      date: lastUpdate,
      format: DATE_TIME_FORMAT,
    }),
    190,
    myY,
    30,
  );

  // Observaciones
  if (description) {
    const descriptionText = doc.splitTextToSize(
      `Observaciones: ${description}`,
      pageWidth - x * 2,
    );

    descriptionText.forEach((text) => {
      addText(doc, text, x, (myY += textSpacing), pageWidth - x * 2, 'left');
    });
  }

  printTableHeader(doc);
};

const addPage = (doc, height, serviceOrder) => {
  const nextY = isNewPageNeeded(doc, x, myY, height);

  if (nextY === 5) {
    page += 1;
    myY = nextY;

    printDocumentHeader(doc, serviceOrder);

    // Validación del espacio después del encabezado.
    // Los comentarios extremadamente largos pueden
    // requerir paginación independiente.
    const pageHeight = doc.internal.pageSize.getHeight();

    if (myY + height > pageHeight - 5) {
      // Bloque demasiado grande para una sola página.
      // No se divide automáticamente.
    }
  } else {
    myY = nextY;
  }
};

// Nombre de actividad: máximo 3 líneas
const getActivityLines = (doc, activityName) => {
  const maxWidth = ACTIVITY_COLUMN_WIDTH - 4;

  const activityLines = doc.splitTextToSize(
    String(activityName || '-'),
    maxWidth,
  );

  if (activityLines.length <= ACTIVITY_MAX_LINES) {
    return activityLines;
  }

  const lines = activityLines.slice(0, ACTIVITY_MAX_LINES);

  let lastLine = lines[ACTIVITY_MAX_LINES - 1].trim();

  // Agregar puntos suspensivos sin desbordar
  while (lastLine.length > 0 && doc.getTextWidth(`${lastLine}...`) > maxWidth) {
    lastLine = lastLine.slice(0, -1).trimEnd();
  }

  lines[ACTIVITY_MAX_LINES - 1] = `${lastLine}...`;

  return lines;
};

const printTableBody = (doc, warning, serviceOrder) => {
  const {
    issueDate,
    subCategory,
    priority,
    reporterCustomer,
    id,
    creationType,
    comments,
    activityBus,
  } = warning;

  const maintenancePlanningActivityResource =
    warning.maintenancePlanningActivityResource || {};

  const maintenancePlanningActivityOrder =
    maintenancePlanningActivityResource.maintenancePlanningActivityOrder ||
    null;

  const activityData = maintenancePlanningActivityOrder || activityBus || {};

  const activityName =
    activityData.activity && activityData.activity.name
      ? activityData.activity.name
      : '-';

  const companyActivityId =
    activityData.activity && activityData.activity.companyActivityId
      ? activityData.activity.companyActivityId
      : '-';

  // Configuración de fuente
  doc.setFontSize(paragraphSize);
  setFont(doc);

  // Obtener hasta 3 líneas
  const activityLines = getActivityLines(doc, activityName);

  // Preparar comentarios
  const commentLines = comments
    ? doc.splitTextToSize(`Comentarios: ${comments}`, pageWidth - x * 2 - 2)
    : [];

  const commentHeight = commentLines.length
    ? COMMENT_PADDING * 2 + textSpacing * commentLines.length
    : 0;

  // Altura total de cada bloque
  const newHeight =
    ROW_SPACING +
    ROW_HEIGHT +
    commentHeight +
    RESULT_TOP_SPACING +
    RESULT_HEIGHT +
    RESULT_SPACING;

  // Validación de paginación
  addPage(doc, newHeight, serviceOrder);

  doc.setFontSize(paragraphSize);
  setFont(doc);

  // ========================================
  // FILA PRINCIPAL DE ACTIVIDAD
  // ========================================

  const rowTop = myY + ROW_SPACING;
  const rowBottom = rowTop + ROW_HEIGHT;

  const rowTextY = rowTop + ROW_HEIGHT / 2 + 1;

  doc.rect(x, rowTop, pageWidth - 10, ROW_HEIGHT);

  // Fecha
  addText(
    doc,
    tzNormalizeDate({
      date: issueDate,
      format: DATE_FORMAT,
    }),
    13,
    rowTextY,
    16,
    'center',
  );

  // Tipo de actividad
  addText(
    doc,
    creationType ? creationType.substring(0, 3).concat('.') : '-',
    30,
    rowTextY,
    16,
    'center',
  );

  // Subcategoría
  const subCategoryName = subCategory
    ? reduceText(doc, subCategory.name, 28)
    : '-';

  addText(doc, subCategoryName, 53, rowTextY, 28, 'center');

  // ID de actividad
  addText(doc, String(companyActivityId), 76, rowTextY, 17, 'center');

  // ========================================
  // NOMBRE DE ACTIVIDAD - HASTA 3 LÍNEAS
  // ========================================

  const activityStartY =
    rowTextY - ((activityLines.length - 1) * ACTIVITY_LINE_HEIGHT) / 2;

  activityLines.forEach((line, index) => {
    addText(
      doc,
      line,
      115,
      activityStartY + index * ACTIVITY_LINE_HEIGHT,
      ACTIVITY_COLUMN_WIDTH,
      'center',
    );
  });

  // ========================================
  // PRIORIDAD
  // ========================================

  const priorityConfig = MAINTENANCE_SERVICE_ORDER_PRIORITY[priority] || {};

  doc.setTextColor(priorityConfig.hexColor || COLOR.black);

  addText(doc, priorityConfig.label || '-', 152, rowTextY, 16, 'center');

  doc.setTextColor(COLOR.black);

  // Reportador
  const reporterName = reduceText(
    doc,
    reporterCustomer && reporterCustomer.fullName
      ? reporterCustomer.fullName
      : '-',
    28,
  );

  addText(doc, reporterName, 175, rowTextY, 28, 'center');

  // ID de advertencia
  addText(
    doc,
    id !== null && id !== undefined ? String(id) : '-',
    198,
    rowTextY,
    12,
    'center',
  );

  myY = rowBottom;

  // ========================================
  // COMENTARIOS COMPACTOS
  // ========================================

  if (commentLines.length) {
    const commentTop = myY;

    const commentBottom = commentTop + commentHeight;

    doc.line(x, commentTop, x, commentBottom);

    doc.line(pageWidth - x, commentTop, pageWidth - x, commentBottom);

    let commentY = commentTop + COMMENT_PADDING;

    commentLines.forEach((text) => {
      commentY += textSpacing;

      addText(doc, text, x + 1, commentY, pageWidth - x * 2 - 2, 'left');
    });

    myY = commentBottom;

    doc.line(x, myY, pageWidth - x, myY);
  }

  // ========================================
  // RESULTADOS DE MANTENIMIENTO
  // ========================================

  const resultTop = myY + RESULT_TOP_SPACING;

  doc.rect(x, resultTop, 139, RESULT_HEIGHT);

  doc.rect(144, resultTop, 61, RESULT_HEIGHT);

  const resultTextY = resultTop + textSpacing;

  addText(doc, 'Resultados de Mantenimiento:', x + 1, resultTextY, 138, 'left');

  addText(doc, 'Realizado por:', 145, resultTextY, 62, 'left');

  // Posición final del bloque
  myY = resultTop + RESULT_HEIGHT;
};

const printMaintenanceServiceOrder = (data) => {
  const doc = createPdfDocument();

  pageWidth = doc.internal.pageSize.getWidth();

  page = 1;
  myY = initialY;
  serviceOrderId = data.id;

  printDocumentHeader(doc, data);

  (data.maintenanceWarningList || []).forEach((warning) => {
    printTableBody(doc, warning, data);

    myY += RESULT_SPACING;
  });

  window.open(doc.output('bloburl'), '_blank');
};

export default printMaintenanceServiceOrder;
